import React from 'react';
import {DateTime} from 'luxon';

import {Loading} from 'Components/Partials';
import CreateCmsElementRecordModal from 'Components/Partials/Modals/CreateCmsElementRecordModal';

import {Modal, Toast} from 'Services';
import RecordApi from 'Services/Api/Cms/Elements/Record';
import StaffFormsApi from 'Services/Api/HumanResources/StaffForms';
import AdminUsersApi from 'Services/Api/Admin/HumanResources/Users';

const STATUSES = {
    outstanding: {label: 'Outstanding', classes: 'bg-red-100 text-red-800'},
    awaiting_admin: {label: 'Awaiting manager', classes: 'bg-yellow-100 text-yellow-800'},
    complete: {label: 'Complete', classes: 'bg-green-100 text-green-800'},
};

/**
 * A staff member's staff forms, with whether each has been filled in, and buttons to fill in,
 * view or update them. Without userId it shows the logged-in staff member's own forms.
 */
export default class StaffFormsChecklist extends React.Component {
    /**
     * @var state
     */
    state = {
        loading: true,
        checklist: null,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.load();

        RecordApi.on('updated', this.load);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        RecordApi.removeListener('updated', this.load);
    };

    /**
     * @method load
     */
    load = async () => {
        const {userId} = this.props;

        const response = userId ? await AdminUsersApi.getStaffForms(userId) : await StaffFormsApi.get(null);

        if (!response.success) {
            this.setState({loading: false});
            return Toast.error();
        }

        this.setState({loading: false, checklist: response.data.data});
    };

    /**
     * @method fillIn
     * @param {object} item
     */
    fillIn = (item) => {
        Modal.open({
            component: CreateCmsElementRecordModal,
            props: {
                showElementsDropdown: false,
                element_id: item.element.id,
                element_record_id: null,
                service_user_id: null,
                staff_form: true,
                staff_user_id: this.props.userId ?? null,
            },
        });
    };

    /**
     * @method view
     * @param {object} item
     */
    view = (item) => {
        Modal.open({
            component: CreateCmsElementRecordModal,
            props: {
                showElementsDropdown: false,
                element_id: item.element.id,
                element_record_id: item.latest_record.id,
                staff_user_id: this.props.userId ?? null,
            },
        });
    };

    /**
     * @method formatDate
     * @param {string|object} value
     * @return {string}
     */
    formatDate = (value) => {
        const iso = typeof value === 'string' ? value : value?.iso_string;

        return iso ? DateTime.fromISO(iso).toFormat('d LLL yyyy') : '';
    };

    /**
     * @method describe
     * @param {object} item
     * @return {string}
     */
    describe = (item) => {
        const {userId} = this.props;
        const record = item.latest_record;

        if (!record) {
            return userId && !item.element.display_form_on_frontend ?
                'Not filled in yet. Only managers can fill this one in.' :
                'Not filled in yet';
        }

        let text = 'Filled in ' + this.formatDate(record.created_at);

        if (!record.filled_in_by_self && record.filled_in_by) {
            text += ' by ' + record.filled_in_by;
        }

        if (item.records_count > 1) {
            text += ` (${item.records_count} times)`;
        }

        return text;
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {loading, checklist} = this.state;
        const {userId} = this.props;

        if (loading) {
            return <Loading/>;
        }

        if (!checklist) {
            return null;
        }

        const {forms, complete_count, total_count, staff_user} = checklist;
        const percent = total_count ? Math.round((complete_count / total_count) * 100) : 0;

        return (
            <section className="bg-white border border-gray-200 rounded-2xl p-5" aria-labelledby="staff-forms-heading">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 id="staff-forms-heading" className="font-league-spartan font-bold text-2xl">
                            {userId ? `${staff_user.full_name}'s staff forms` : 'My staff forms'}
                        </h2>
                        <div className="text-gray-600">{complete_count} of {total_count} complete</div>
                    </div>
                </div>

                {total_count > 0 && (
                    <div
                        role="img"
                        aria-label={`${complete_count} of ${total_count} staff forms complete`}
                        className="mt-3 h-2.5 rounded-full bg-gray-200 overflow-hidden"
                    >
                        <div className="h-2.5 bg-app-leading" style={{width: percent + '%'}}/>
                    </div>
                )}

                {total_count === 0 && (
                    <p className="mt-4">There are no staff forms to fill in.</p>
                )}

                <ul className="mt-4 divide-y divide-gray-100">
                    {forms.map(item => {
                        const status = STATUSES[item.status];
                        const canFillIn = !!userId || item.element.display_form_on_frontend;

                        return (
                            <li
                                key={item.element.id}
                                className={`flex flex-wrap items-center justify-between gap-3 py-3 px-2 ${item.status === 'outstanding' ? 'bg-red-50 rounded-lg' : ''}`}
                            >
                                <div className="flex-1 min-w-[16rem]">
                                    <div className="font-bold">{item.element.label}</div>
                                    <div className="text-sm text-gray-600">{this.describe(item)}</div>
                                </div>

                                <span className={`text-xs font-bold px-3 py-1 rounded-full ${status.classes}`}>
                                    {status.label}
                                </span>

                                <div className="flex gap-3 justify-end min-w-[12rem]">
                                    {item.latest_record && (
                                        <button type="button" className="font-bold underline" onClick={() => this.view(item)}>
                                            View / update
                                        </button>
                                    )}

                                    {canFillIn && (
                                        <button
                                            type="button"
                                            className={item.latest_record ?
                                                'underline' :
                                                'bg-app-supporting-yellow text-white font-bold rounded-full py-2 px-4'}
                                            onClick={() => this.fillIn(item)}
                                        >
                                            {item.latest_record ? 'Fill in again' : 'Fill in'}
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    }
}
